import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox } from '@fortawesome/pro-light-svg-icons';
import { faGear, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { ChannelFilter, InboxConversationSummary } from '@spike/omnichannel-model';
import clsx from 'clsx';
import MarketplaceSelector, {
    useShowMarketplaceSelector
} from 'components/SMS/TwoWay/MarketplaceSelector/MarketplaceSelector';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner';
import React from 'react';
import ConversationListItem from './ConversationListItem';
import styles from './ConversationList.module.scss';

interface ConversationListProps {
    conversations: Array<InboxConversationSummary>;
    selectedId?: number;
    searchText: string;
    channelFilter: ChannelFilter;
    totalUnread: number;
    loading?: boolean;
    errorMessage?: string | null;
    newConversationIds?: Set<number>;
    selectedMarketplaceId?: number;
    onMarketplaceChange?: (marketplaceId: number) => void;
    onOpenSettings?: () => void;
    onNewMessage?: () => void;
    onSelect: (id: number) => void;
    onSearchTextChange: (text: string) => void;
    onChannelFilterChange: (filter: ChannelFilter) => void;
}

const FILTERS: Array<{ id: ChannelFilter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'sms', label: 'SMS' },
    { id: 'email', label: 'Email' },
    { id: 'unread', label: 'Unread' }
];

export const ConversationList: React.FC<ConversationListProps> = props => {
    const showMarketplaceSelector = useShowMarketplaceSelector();

    return (
        <aside className={styles.root}>
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <h2 className={styles.title}>Inbox</h2>
                    <div className={styles.titleActions}>
                        {props.totalUnread > 0 && <span className={styles.unreadTotal}>{props.totalUnread} new</span>}
                        {props.onNewMessage && (
                            <button
                                type="button"
                                className={styles.headerButton}
                                aria-label="New message"
                                title="New message"
                                onClick={props.onNewMessage}
                            >
                                <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                        )}
                        {props.onOpenSettings && (
                            <button
                                type="button"
                                className={styles.headerButton}
                                aria-label="Inbox settings"
                                title="Inbox settings"
                                onClick={props.onOpenSettings}
                            >
                                <FontAwesomeIcon icon={faGear} />
                            </button>
                        )}
                    </div>
                </div>

                {showMarketplaceSelector && props.selectedMarketplaceId !== undefined && props.onMarketplaceChange && (
                    <div className={styles.locationRow}>
                        <MarketplaceSelector
                            selectedMarketplaceId={props.selectedMarketplaceId}
                            onChange={props.onMarketplaceChange}
                        />
                    </div>
                )}

                <SearchBar
                    placeholder="Search conversations"
                    searchText={props.searchText}
                    onChangeSearchText={props.onSearchTextChange}
                />

                <div className={styles.filters}>
                    {FILTERS.map(filter => (
                        <button
                            key={filter.id}
                            type="button"
                            className={clsx(styles.filter, props.channelFilter === filter.id && styles.filterActive)}
                            onClick={() => props.onChannelFilterChange(filter.id)}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.items}>
                {props.loading && props.conversations.length === 0 && (
                    <div className={styles.empty}>
                        <Spinner />
                    </div>
                )}

                {props.conversations.map(conversation => (
                    <ConversationListItem
                        key={conversation.id}
                        conversation={conversation}
                        selected={conversation.id === props.selectedId}
                        isNew={props.newConversationIds?.has(conversation.id)}
                        onSelect={props.onSelect}
                    />
                ))}

                {!props.loading && props.conversations.length === 0 && (
                    <div className={styles.empty}>
                        <FontAwesomeIcon
                            icon={faInbox}
                            className={styles.emptyIcon}
                        />
                        <p>{props.errorMessage ?? 'No conversations found'}</p>
                    </div>
                )}
            </div>
        </aside>
    );
};

export default ConversationList;
